import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../apiClient";
import { Link } from "react-router-dom";
import { formatMoney } from "../formatMoney";

const Assets = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "Description", direction: "asc" });

  const assetTotals = useMemo(() => {
    const totals = assets.reduce((acc, asset) => {
      const currency = asset.CurrencyDescription || "Unspecified";
      acc[currency] = (acc[currency] || 0) + Number(asset.CurrentValue || 0);
      return acc;
    }, {});
    return Object.entries(totals).map(([currency, total]) => ({ currency, total }));
  }, [assets]);

  const filteredAssets = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = !query ? assets : assets.filter((asset) =>
      [asset.Description, asset.CurrencyDescription, asset.CurrentValuationDate]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
    return [...filtered].sort((left, right) => {
      const leftValue = left[sortConfig.key];
      const rightValue = right[sortConfig.key];
      const leftNumeric = Number(leftValue);
      const rightNumeric = Number(rightValue);
      const comparison = Number.isFinite(leftNumeric) && Number.isFinite(rightNumeric)
        ? leftNumeric - rightNumeric
        : String(leftValue || "").localeCompare(String(rightValue || ""));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [assets, search, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => (
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    ));
  };

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const res = await apiClient.get("/assets");
        setAssets(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError("Failed to load assets.");
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, []);

  const handleDelete = async (assetId) => {
    try {
      await apiClient.delete(`/assets/${assetId}`);
      setAssets((prev) => prev.filter((item) => item.AssetID !== assetId));
    } catch (err) {
      setError("Delete failed. Please try again.");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Assets</h1>
          <p className="page-subtitle"><Link to="/">Home</Link> / Assets</p>
        </div>
        <Link className="btn-link" to="/AssetsAdd">Add Asset</Link>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading assets...</p>}

      {!loading && (
        <section className="resource-list">
          {assets.length === 0 ? (
            <article className="panel empty-state">
              <h2>No assets yet</h2>
              <p className="notice notice-muted">Add an asset to start tracking current valuations and include it in net worth reporting.</p>
              <div className="form-actions">
                <Link className="btn" to="/AssetsAdd">Add First Asset</Link>
              </div>
            </article>
          ) : (
            <>
              <article className="panel">
                <h2>Total Asset Value</h2>
                <div className="dashboard-grid">
                  {assetTotals.map(({ currency, total }) => (
                    <div key={currency} className="metric-card metric-card-compact">
                      <strong>{currency}</strong>
                      <div className="resource-meta">{formatMoney(total, currency)}</div>
                    </div>
                  ))}
                </div>
              </article>

              <section className="panel page-cta list-filter-panel">
                <div className="list-filters">
                  <label className="list-filter-field">
                    <span>Search</span>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Find an asset"
                    />
                  </label>
                </div>
              </section>

              <div className="table-scroll">
                <table className="resource-table">
                  <thead>
                    <tr>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("Description")}>Asset</button></th>
                      <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("PurchaseValue")}>Purchase Value</button></th>
                      <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("CurrentValue")}>Current Value</button></th>
                      <th><button type="button" className="table-sort" onClick={() => handleSort("CurrentValuationDate")}>Valuation Date</button></th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map((asset) => (
                      <tr key={asset.AssetID}>
                        <td data-label="Asset"><strong>{asset.Description}</strong></td>
                        <td data-label="Purchase Value" className="table-number table-money">{formatMoney(asset.PurchaseValue, asset.CurrencyDescription)}</td>
                        <td data-label="Current Value" className="table-number table-money">{formatMoney(asset.CurrentValue, asset.CurrencyDescription)}</td>
                        <td data-label="Valuation Date">{asset.CurrentValuationDate || "-"}</td>
                        <td data-label="Actions" className="resource-table-actions">
                          <Link className="btn-link" to={`/AssetsUpdate/${asset.AssetID}`}>Edit</Link>
                          <button className="action-link action-link-danger" onClick={() => handleDelete(asset.AssetID)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default Assets;
